import * as React from 'react';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

type SkeletonVariant = 'text' | 'rectangular' | 'rounded' | 'circular';
type SkeletonAnimation = 'pulse' | 'wave' | false;

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: number | string;
  height?: number | string;
  variant?: SkeletonVariant;
  animation?: SkeletonAnimation;
  sx?: React.CSSProperties;
  /**
   * Render the child element (for example a `<span>` inside a `<p>`) with
   * the skeleton's classes and style, instead of a `<div>`.
   */
  asChild?: boolean;
}

// `wave`: a faint highlight sweeps across the bone (the former MUI Skeleton
// wave), darker ink on light surfaces and lighter ink in the dark theme. The
// pseudo-element starts off the left edge with `transform`, the property the
// `skeleton-wave` keyframes (`_animations.scss`) animate. Reduced motion
// hides it.
const WAVE_CLASSES = [
  'relative overflow-hidden [-webkit-mask-image:-webkit-radial-gradient(white,black)]',
  "after:absolute after:inset-0 after:[transform:translateX(-100%)] after:animate-[skeleton-wave_2s_linear_0.5s_infinite] after:content-['']",
  'after:bg-[linear-gradient(90deg,transparent,rgba(0,0,0,0.04),transparent)] [.theme-dark_&]:after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)]',
  'motion-reduce:after:hidden',
].join(' ');

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      className,
      width,
      height,
      variant = 'rectangular',
      animation = 'pulse',
      sx,
      style,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const borderRadius =
      variant === 'circular'
        ? '9999px'
        : variant === 'rounded'
          ? '0.5rem'
          : variant === 'text'
            ? '0.25rem'
            : undefined;

    const mergedStyle: React.CSSProperties = {
      width,
      height: variant === 'text' ? (height ?? '1em') : height,
      borderRadius,
      ...(sx ?? {}),
      ...(style ?? {}),
    };

    const Comp = asChild ? Slot.Root : 'div';

    return (
      <Comp
        ref={ref}
        aria-hidden="true"
        className={cn(
          'ui-skeleton',
          'bg-[var(--surface-tint)]',
          animation === 'pulse' && 'animate-pulse',
          animation === 'wave' && WAVE_CLASSES,
          className
        )}
        style={mergedStyle}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export { Skeleton };
export default Skeleton;
